import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/idioma';
import type { RetratoDoMembro } from '../realtime/cityHub';

/** O prazo no relógio local: o servidor manda a hora dele; fora de uma faixa sã, vale o padrão de 30 s. */
function prazoLocal(expiraEmMs: number): number {
  const agora = Date.now();
  const falta = expiraEmMs - agora;
  return falta > 0 && falta <= 130_000 ? expiraEmMs : agora + 30_000;
}

function useSegundos(ate: number): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);
  return Math.max(0, Math.ceil((ate - agora) / 1000));
}

function rotuloModo(modo: string): string {
  return modo === 'com_loot' ? t('duelo.comLoot') : t('duelo.semLoot');
}

/**
 * O convite de duelo para quem recebe (duelo v2): um cartão no topo que não trava a tela, com o retrato, o modo, a
 * aposta e o relógio do prazo. Some quando o servidor fecha o convite (DueloConviteFim) ou o prazo acaba.
 */
export function DueloConviteCard({ de, modo, comAposta, expiraEmMs, retratoDe, onAccept, onRefuse }: {
  de: string;
  modo: string;
  comAposta: boolean;
  expiraEmMs: number;
  retratoDe(canvas: HTMLCanvasElement, nome: string, look: RetratoDoMembro | null): () => void;
  onAccept(): void;
  onRefuse(): void;
}) {
  const ate = useRef(prazoLocal(expiraEmMs)).current;
  const total = Math.max(1, Math.round((ate - Date.now()) / 1000));
  const totalRef = useRef(total).current;
  const s = useSegundos(ate);
  const cv = useRef<HTMLCanvasElement>(null);
  const pintar = useRef(retratoDe);
  pintar.current = retratoDe;
  useEffect(() => {
    const c = cv.current;
    if (!c) return;
    return pintar.current(c, de, null);
  }, [de]);
  if (s <= 0) return null;
  const loot = modo === 'com_loot';
  const r = 16;
  const c = 2 * Math.PI * r;
  return (
    <div className="dz-convite" role="alertdialog" aria-labelledby="dz-convite-t">
      <div className="dz-convite__cab">
        <canvas ref={cv} className="dz-retrato" width={52} height={52} aria-hidden="true" />
        <div className="dz-convite__t">
          <b id="dz-convite-t">{t('duelo.convite2.titulo', { nome: de })}</b>
          <div className="dz-tags">
            <span className={`dz-chip${loot ? ' dz-chip--loot' : ''}`}>{rotuloModo(modo)}</span>
            <span className={`dz-chip${comAposta ? ' dz-chip--aposta' : ''}`}>{comAposta ? t('duelo.comAposta') : t('duelo.semAposta')}</span>
            <span className="dz-chip">{t('duelo.melhorDe', { n: 3 })}</span>
          </div>
        </div>
        <div className="dz-relogio" aria-label={t('duelo.convite2.prazo', { s })}>
          <svg viewBox="0 0 38 38" aria-hidden="true">
            <circle className="fundo" cx="19" cy="19" r={r} />
            <circle className="arco" cx="19" cy="19" r={r} strokeDasharray={c.toFixed(1)} strokeDashoffset={(c * (1 - s / totalRef)).toFixed(1)} />
          </svg>
          <span>{s}</span>
        </div>
      </div>
      <p className="dz-convite__linha">
        {loot ? t('duelo.convite2.loot') : t('duelo.convite2.amistoso')}{' '}
        {comAposta ? t('duelo.convite2.comAposta') : t('duelo.convite2.semAposta')}
      </p>
      <div className="dz-convite__pe">
        <button type="button" className="btn" onClick={onRefuse}>{t('comum.recusar')}</button>
        <button type="button" className="btn btn--primary" onClick={onAccept}>⚔ {t('comum.aceitar')}</button>
      </div>
    </div>
  );
}

/** A pílula de quem desafiou: esperando a resposta, com o prazo e o Cancelar (duelo v2). Não trava a tela. */
export function DueloEnviadoPilula({ para, modo, comAposta, expiraEmMs, onCancelar }: {
  para: string;
  modo: string;
  comAposta: boolean;
  expiraEmMs: number;
  onCancelar(): void;
}) {
  const ate = useRef(prazoLocal(expiraEmMs)).current;
  const s = useSegundos(ate);
  if (s <= 0) return null;
  return (
    <div className="dz-espera" role="status">
      <span className="dz-espera__sw" aria-hidden="true">⚔</span>
      <span>{t('duelo.enviado2', { nome: para, modo: rotuloModo(modo) })}{comAposta ? ` · ${t('duelo.comAposta').toLowerCase()}` : ''}</span>
      <span className="dz-espera__tempo">0:{String(s).padStart(2, '0')}</span>
      <button type="button" className="dz-mini-btn" onClick={onCancelar}>{t('comum.cancelar')}</button>
    </div>
  );
}
